import cn from 'classnames'
import { useSelect } from 'downshift'
import DropdownMenu from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu'
import DropdownOption, {
  DropdownOptionModel,
} from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import { DropdownValue } from 'ext-ui/components/Dropdown/hooks/DropdownState.reducer'
import { TextInputVariant } from 'ext-ui/components/TextInput/TextInput'
import ChevronIcon from 'ext-ui/svg/icon/ChevronIcon'
import React, { useId, useRef } from 'react'

import style from './InlineDropdown.style'

export type { DropdownValue }

export type InlineDropdownProps<T = unknown> = {
  options: DropdownOptionModel<T>[]
  selectedValue: DropdownValue<T> | undefined
  onSelect: (value: DropdownValue<T>) => void
  /** Already-translated accessible name for the control, e.g. "Currency". */
  ariaLabel: string
  /** Already-translated placeholder shown when no option is selected. */
  placeholder?: string
  disabled?: boolean
  variant?: TextInputVariant
  className?: string
  'data-testid': string
}

function InlineDropdown<T = unknown>(
  props: InlineDropdownProps<T>,
): React.ReactElement {
  const {
    options,
    selectedValue,
    onSelect,
    ariaLabel,
    placeholder,
    disabled,
    variant = 'medium',
    className,
    'data-testid': testId,
  } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const labelId = useId()

  const selectedItem =
    options.find((o) => o.value === selectedValue?.value) ?? null

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
  } = useSelect<DropdownOptionModel<T>>({
    items: options,
    labelId,
    selectedItem,
    itemToString: (o) => o?.label ?? o?.value ?? '',
    isItemDisabled: (o) => !!o?.disabled,
    onSelectedItemChange: ({ selectedItem: item }) => {
      if (item)
        onSelect({
          value: item.value,
          label: item.label,
          details: item.details,
        })
    },
  })

  const isDisabled = disabled || options.length === 0

  return (
    <div ref={rootRef} className={cn('InlineDropdown', className)} css={style}>
      <span className="visually-hidden" id={labelId}>
        {ariaLabel}
      </span>
      <button
        {...getToggleButtonProps({
          type: 'button',
          className: 'inline-trigger',
          disabled: isDisabled,
          'data-testid': `${testId}-trigger`,
        })}
      >
        <span>
          {selectedItem?.label ??
            selectedItem?.value ??
            selectedValue?.label ??
            selectedValue?.value ??
            placeholder ??
            ''}
        </span>
        <ChevronIcon
          className="inline-chevron"
          direction={isOpen ? 'up' : 'down'}
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
      <DropdownMenu
        {...getMenuProps({}, { suppressRefError: true })}
        anchorEl={rootRef.current}
        variant={variant}
        open={isOpen}
        menuTestId={testId}
      >
        {options.map((opt, index) => (
          <DropdownOption
            key={opt.value}
            {...opt}
            selected={opt.value !== '' && opt.value === selectedValue?.value}
            highlighted={highlightedIndex === index}
            variant={variant}
            data-testid={`${testId}-option-${opt.value}`}
            data-name={typeof opt.label === 'string' ? opt.label : opt.value}
            {...getItemProps({ item: opt, index })}
          />
        ))}
      </DropdownMenu>
    </div>
  )
}

export default InlineDropdown
